
import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  Clock,
  Plus,
  Search,
  User,
  X,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MentorSession } from "@/types/database-extensions";

const MentorMoments = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [sessions, setSessions] = useState<MentorSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSession, setNewSession] = useState({
    title: "",
    date: "",
    time: "",
    duration: "60 min",
    category: "",
    description: "",
    spots: 5
  });
  const [registering, setRegistering] = useState<string | null>(null);
  const [savingSession, setSavingSession] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user, activeTab]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      
      // Query for real sessions from the database
      // For demonstration, we'll use an empty array instead of sample data
      const { data, error } = await supabase
        .from("mentors")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Convert mentor data to session format - in a real app this would be joined data
      const mentorSessions: MentorSession[] = [];
      
      setSessions(mentorSessions);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    if (!user) return;
    if (!newSession.title || !newSession.date || !newSession.time || !newSession.category) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setSavingSession(true);
      
      // Create new session
      const sessionData = {
        title: newSession.title,
        date: newSession.date,
        time: newSession.time,
        duration: newSession.duration,
        category: newSession.category,
        description: newSession.description,
        spots: newSession.spots,
        participants: 0,
        mentor_id: user.id,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from("mentor_sessions")
        .insert(sessionData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Add to local state
      setSessions([data as MentorSession, ...sessions]);
      
      // Close dialog and reset form
      setShowCreateDialog(false);
      setNewSession({
        title: "",
        date: "",
        time: "",
        duration: "60 min",
        category: "",
        description: "",
        spots: 5
      });
      
      toast({
        title: "Session Created",
        description: "Your mentoring session has been added to the calendar",
      });
    } catch (error) {
      console.error("Error creating session:", error);
      toast({
        title: "Error",
        description: "Could not create session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingSession(false);
    }
  };

  const registerForSession = async (sessionId: string) => {
    if (!user) return;
    
    try {
      setRegistering(sessionId);
      
      // Find session to register for
      const session = sessions.find(s => s.id === sessionId);
      if (!session) return;
      
      // In a real app, we'd create a registration record
      // and update the participants count
      
      // For now, just show a success toast
      toast({
        title: "Registered Successfully",
        description: `You have registered for ${session.title}`,
      });
      
      // Award karma points for registering for a session
      await supabase
        .from("karma_activities")
        .insert({
          user_id: user.id,
          title: "Registered for a mentor session",
          points: 20,
          created_at: new Date().toISOString()
        });
    } catch (error) {
      console.error("Error registering for session:", error);
      toast({
        title: "Error",
        description: "Could not register for this session",
        variant: "destructive",
      });
    } finally {
      setRegistering(null);
    }
  };

  const filteredSessions = sessions.filter((session) => {
    // Filter by search query (case insensitive)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        session.title.toLowerCase().includes(query) ||
        session.category.toLowerCase().includes(query) ||
        (session.description && session.description.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // Filter sessions based on active tab
  const displayedSessions = filteredSessions.filter((session) => {
    const sessionDate = new Date(`${session.date}T${session.time}`);
    const now = new Date();
    
    if (activeTab === "upcoming") {
      return sessionDate > now;
    } else if (activeTab === "past") {
      return sessionDate <= now;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sessions..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Session
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create a Mentoring Session</DialogTitle>
              <DialogDescription>
                Share your knowledge and help others by hosting a mentoring session
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div>
                <label htmlFor="title" className="text-sm font-medium block mb-1">
                  Title *
                </label>
                <Input
                  id="title"
                  value={newSession.title}
                  onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                  placeholder="E.g., College Application Tips"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="date" className="text-sm font-medium block mb-1">
                    Date *
                  </label>
                  <Input
                    id="date"
                    type="date"
                    value={newSession.date}
                    onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                    min={format(new Date(), "yyyy-MM-dd")}
                  />
                </div>
                <div>
                  <label htmlFor="time" className="text-sm font-medium block mb-1">
                    Time *
                  </label>
                  <Input
                    id="time"
                    type="time"
                    value={newSession.time}
                    onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="duration" className="text-sm font-medium block mb-1">
                    Duration
                  </label>
                  <Input
                    id="duration"
                    value={newSession.duration}
                    onChange={(e) => setNewSession({ ...newSession, duration: e.target.value })}
                    placeholder="E.g., 60 min"
                  />
                </div>
                <div>
                  <label htmlFor="spots" className="text-sm font-medium block mb-1">
                    Spots Available
                  </label>
                  <Input
                    id="spots"
                    type="number"
                    min="1"
                    value={newSession.spots}
                    onChange={(e) => setNewSession({ ...newSession, spots: parseInt(e.target.value) || 5 })}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="category" className="text-sm font-medium block mb-1">
                  Category *
                </label>
                <Input
                  id="category"
                  value={newSession.category}
                  onChange={(e) => setNewSession({ ...newSession, category: e.target.value })}
                  placeholder="E.g., Career Advice, Academics"
                />
              </div>
              
              <div>
                <label htmlFor="description" className="text-sm font-medium block mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                  value={newSession.description}
                  onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                  placeholder="Describe what participants can expect from this session"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={createSession}
                disabled={savingSession || !newSession.title || !newSession.date || !newSession.time || !newSession.category}
              >
                {savingSession && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="past">Past</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="space-y-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : displayedSessions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedSessions.map((session) => (
                <Card key={session.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{session.title}</CardTitle>
                      <Badge variant="outline">{session.category}</Badge>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{session.date}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <div className="flex gap-4 mb-3 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{session.time}</span>
                      </div>
                      <div className="text-muted-foreground">
                        {session.duration}
                      </div>
                    </div>
                    {session.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{session.description}</p>
                    )}
                  </CardContent>
                  <CardFooter className="border-t pt-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback>
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">
                        <span className="font-medium">
                          {session.participants}
                        </span>{" "}
                        of {session.spots} spots filled
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => registerForSession(session.id)}
                      disabled={registering === session.id}
                    >
                      {registering === session.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Join"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-12 border rounded-md bg-background">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Upcoming Sessions</h3>
              <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
                There are no upcoming mentoring sessions scheduled yet. Why not create one to share your knowledge?
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create a Session
              </Button>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="past" className="space-y-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : displayedSessions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Similar card layout for past sessions, but with different action buttons */}
              {displayedSessions.map((session) => (
                <Card key={session.id} className="overflow-hidden opacity-80">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{session.title}</CardTitle>
                      <Badge variant="outline">{session.category}</Badge>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{session.date}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <div className="flex gap-4 mb-3 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{session.time}</span>
                      </div>
                      <div className="text-muted-foreground">
                        {session.duration}
                      </div>
                    </div>
                    {session.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{session.description}</p>
                    )}
                  </CardContent>
                  <CardFooter className="border-t pt-4">
                    <Button variant="outline" size="sm" className="w-full">
                      View Recording
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-12 border rounded-md bg-background">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <X className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Past Sessions</h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">
                There are no past mentoring sessions yet. Check back later or register for an upcoming session.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MentorMoments;
